import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React from 'react';

import formatPrice from '../../../util/formatPrice';
import Button from '../Atoms/Button';
import ErrorMessage from '../Core/ErrorMessage';
import { DEFAULT_CRITICAL_BACKUP_CURRENCY } from '../../../util/currency';
import Picture from '../Picture';
import SummaryContent from '../PackageSummary/SummaryContent';

const propTypes = exact({
  currencySymbol: PropTypes.string.isRequired,
  optionBankDepositText: PropTypes.string,
  preferredCardImage: PropTypes.object,
  price: PropTypes.number.isRequired,
  priceDeposit: PropTypes.number,
  priceText: PropTypes.string,
  cardPaymentHeadingText: PropTypes.string,
  cardPaymentDescriptionText: PropTypes.string,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  payDepositButtonText: PropTypes.string.isRequired,
  payInFullButtonText: PropTypes.string.isRequired,
  invoiceCtaButtonText: PropTypes.string,
  isSalesAgentBooking: PropTypes.bool,
  isSalesAgentUserLoggedIn: PropTypes.bool,
  invoicePaymentsEnabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  showButtons: PropTypes.bool,
  showDepositCta: PropTypes.bool,
  showPayInFullCta: PropTypes.bool,
  showInvoiceCta: PropTypes.bool,
  showPreferredCardImage: PropTypes.bool,
  bankTransferPaymentsEnabled: PropTypes.bool,
  allowsDeposit: PropTypes.bool,
  bankTransferPaymentHeadingText: PropTypes.string,
  bankTransferPaymentDescriptionText: PropTypes.string,
  bankTransferPaymentImages: PropTypes.arrayOf(
    PropTypes.shape({
      alt: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  cardPaymentImages: PropTypes.arrayOf(
    PropTypes.shape({
      alt: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  submitContainerRef: PropTypes.object,
  waystoPay: PropTypes.string,
  depositText: PropTypes.string,
  summaryContent: PropTypes.shape({
    title: PropTypes.string,
    fromDate: PropTypes.string,
    toDate: PropTypes.string,
    description: PropTypes.string,
    secondDescription: PropTypes.string,
    showAtolLabel: PropTypes.bool,
    atolProtectedText: PropTypes.string,
  }),
});

const defaultProps = {
  cardPaymentHeadingText: 'Card Payment',
  cardPaymentDescriptionText: 'Some text around choosing card payment',
  optionBankDepositText: 'Bank transfer - Pay Deposit',
  preferredCardImage: null,
  priceDeposit: null,
  priceText: 'Total payable',
  isLoading: false,
  invoiceCtaButtonText: null,
  isSalesAgentBooking: false,
  isSalesAgentUserLoggedIn: false,
  invoicePaymentsEnabled: false,
  errorMessage: null,
  showButtons: true,
  showDepositCta: true,
  showPayInFullCta: true,
  showInvoiceCta: true,
  showPreferredCardImage: true,
  bankTransferPaymentsEnabled: false,
  allowsDeposit: false,
  bankTransferPaymentHeadingText: null,
  bankTransferPaymentDescriptionText: null,
  bankTransferPaymentImages: [],
  cardPaymentImages: [],
  submitContainerRef: null,
  waystoPay: 'Ways to Pay',
  depositText: 'Deposit',
  summaryContent: null,
};

function PaymentOptions(props) {
  const hasDepositToken = props.depositText?.includes('{DepositPrice}');
  const formattedDepositPrice = `${
    props.currencySymbol || DEFAULT_CRITICAL_BACKUP_CURRENCY.symbol
  }${formatPrice(props.priceDeposit || 0)}`;
  const depositDisplayText = hasDepositToken
    ? props.depositText.replace('{DepositPrice}', formattedDepositPrice)
    : `${props.depositText}: ${formattedDepositPrice}`;

  return (
    <div className="PaymentOptions">
      <div className="PackageSummary">
        <div className="PaymentOptions_wrap">
          <div className="PackageSummary_body">
            {props.summaryContent && (
              <div className="PackageSummary_item PackageSummary_item-overview">
                <div className="PackageSummary_content">
                  <SummaryContent {...props.summaryContent} />
                </div>
              </div>
            )}

            <div className="PackageSummary_item PackageSummary_item-price price-container">
              <div className="PackageSummary_content">
                <div className="PackageSummary_priceTitle">{props.priceText}:</div>
                <div className="PackageSummary_price">
                  {props.currencySymbol || DEFAULT_CRITICAL_BACKUP_CURRENCY.symbol}
                  {formatPrice(props.price)}
                </div>
              </div>

              {props.allowsDeposit && (
                <div className="PaymentOptions_depositValue">
                  <div>{depositDisplayText}</div>
                </div>
              )}
            </div>
            {props.showPreferredCardImage && props.preferredCardImage && (
              <div className="PackageSummary_item PackageSummary_item-cta">
                <div className="PackageSummary_content">
                  <div className="PaymentOptions_paymentCard">
                    <div className="PackageSummary_preferredCard">
                      <Picture
                        {...props.preferredCardImage}
                        sizes={[
                          ['150', 1], // from 0
                          ['150', 1], // from 440
                          ['150', 1], // from 768
                          ['150', 1], // from 1024
                          ['150', 1], // from 1440
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {props.errorMessage && (
        <div className="Booking_item">
          <div className="Booking_group">
            <ErrorMessage text={props.errorMessage} />
          </div>
        </div>
      )}

      {props.showButtons && (
        <div className="PaymentOptions_item PaymentOptions_item-center PaymentOptions_actionButtons">
          {props.showDepositCta && props.allowsDeposit && (
            <Button
              buttonType="submit"
              handleClick={(event) => props.onSubmit(event, 1)}
              isLoading={props.isLoading}
              modifier="outline border-button"
              text={props.payDepositButtonText}
            />
          )}

          {props.showPayInFullCta &&
            (!props.isSalesAgentBooking || props.isSalesAgentUserLoggedIn) && (
              <Button
                buttonType="submit"
                handleClick={(event) => props.onSubmit(event, 0)}
                isLoading={props.isLoading}
                modifier="fill"
                text={props.payInFullButtonText}
              />
            )}

          {props.showInvoiceCta && props.invoicePaymentsEnabled && props.invoiceCtaButtonText && (
            <Button
              buttonType="submit"
              handleClick={(event) => props.onSubmit(event, 2)}
              isLoading={props.isLoading}
              modifier="outline border-button"
              text={props.invoiceCtaButtonText}
            />
          )}
        </div>
      )}

      <div className="MakePayment_waysToPayWrapper" ref={props.submitContainerRef}>
        <div className="MakePayment_item MakePayment_waysToPay">
          <div className="MakePayment_groupTitle">{props.waystoPay}</div>
        </div>

        <div className="MakePayment_waysToPayContainer">
          <div className="MakePayment_creditCards">
            <div className="MakePayment_waysToPayCaption">
              <div className="MakePayment_waysToPayTitle">{props.cardPaymentHeadingText}</div>
              <div className="MakePayment_waysToPayDescription">
                {props.cardPaymentDescriptionText}
              </div>
            </div>

            <div className="MakePayment_paymentIcons">
              {props.cardPaymentImages?.map((image) => (
                <div key={image.alt} className="MakePayment_creditCards-item">
                  <img className="MakePayment_creditCardImage" src={image.url} alt={image.alt} />
                </div>
              ))}
            </div>
          </div>

          {props.bankTransferPaymentsEnabled && (
            <div className="MakePayment_creditCards">
              <div className="MakePayment_waysToPayCaption">
                <div className="MakePayment_waysToPayTitle">
                  {props.bankTransferPaymentHeadingText}
                </div>
                <div className="MakePayment_waysToPayDescription">
                  {props.bankTransferPaymentDescriptionText}
                </div>
              </div>

              <div className="MakePayment_paymentIcons">
                {props.bankTransferPaymentImages?.map((image) => (
                  <div key={image.alt} className="MakePayment_creditCards-item">
                    <img className="MakePayment_creditCardImage" src={image.url} alt={image.alt} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

PaymentOptions.propTypes = propTypes;
PaymentOptions.defaultProps = defaultProps;

export default PaymentOptions;
